<template>
  <div class="auth">
    <div class="card auth__card">
      <template v-if="step === 'credentials'">
        <h1 class="auth__title">{{ t("auth.signInTitle") }}</h1>
        <p class="auth__lede muted small">{{ t("auth.signInLede") }}</p>
      </template>
      <template v-else-if="step === 'verify'">
        <h1 class="auth__title">{{ t("auth.verifyTitle") }}</h1>
        <p class="auth__lede muted small">{{ t("auth.otpSent", { email }) }}</p>
      </template>
      <template v-else>
        <h1 class="auth__title">{{ t("auth.totpTitle") }}</h1>
        <p class="auth__lede muted small">{{ backup ? t("auth.backupLede") : t("auth.totpLede") }}</p>
      </template>

      <div v-if="notice" class="notice notice--success auth__error">
        <div class="notice__body">{{ notice }}</div>
      </div>
      <div v-if="error" class="notice notice--danger auth__error">
        <div class="notice__body">{{ error }}</div>
      </div>

      <form v-if="step === 'credentials'" class="stack" @submit.prevent="submitCredentials">
        <div class="field">
          <label class="field__label" for="email">{{ t("auth.email") }}</label>
          <input id="email" v-model.trim="email" class="input" type="email" autocomplete="username" required />
        </div>
        <div class="field">
          <label class="field__label" for="password">{{ t("auth.password") }}</label>
          <input id="password" v-model="password" class="input" type="password" autocomplete="current-password" required />
        </div>
        <button class="btn btn--block" type="submit" :disabled="busy">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ busy ? t("common.loading") : t("auth.signInTitle") }}
        </button>
      </form>

      <form v-else-if="step === 'verify'" class="stack" @submit.prevent="submitOtp">
        <div class="field">
          <label class="field__label" for="otp">{{ t("auth.otp") }}</label>
          <input id="otp" v-model.trim="otp" class="input input--mono" inputmode="numeric"
                 autocomplete="one-time-code" maxlength="6" required autofocus />
        </div>
        <button class="btn btn--block" type="submit" :disabled="busy || otp.length < 6">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ t("auth.verifyAction") }}
        </button>
        <p class="small muted">
          <button type="button" class="auth__switch" :disabled="busy" @click="resendOtp">{{ t("auth.resend") }}</button>
        </p>
      </form>

      <form v-else class="stack" @submit.prevent="submitTotp">
        <div class="field">
          <label class="field__label" for="totp">{{ backup ? t("auth.backupCode") : t("auth.totp") }}</label>
          <input id="totp" v-model.trim="code" class="input input--mono"
                 :inputmode="backup ? 'text' : 'numeric'" autocomplete="one-time-code"
                 :maxlength="backup ? 32 : 6" required autofocus />
        </div>
        <label v-if="!backup" class="checkbox">
          <input v-model="trustDevice" type="checkbox" />
          <span>{{ t("auth.trustDevice") }}</span>
        </label>
        <button class="btn btn--block" type="submit" :disabled="busy || !code">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ t("auth.verifyAction") }}
        </button>
        <p class="small muted">
          <button type="button" class="auth__switch" @click="toggleBackup">
            {{ backup ? t("auth.useTotp") : t("auth.useBackup") }}
          </button>
        </p>
      </form>

      <p class="auth__foot small muted">
        <template v-if="step === 'credentials'">
          {{ t("auth.noAccount") }}
          <router-link to="/sign-up">{{ t("nav.signUp") }}</router-link>
        </template>
        <button v-else type="button" class="auth__switch" @click="restart">{{ t("auth.startOver") }}</button>
      </p>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { authApi } from "../lib/api";
import { signInWithPassword, completeTwoFactor, verifyEmailOtp } from "../lib/session";
import { useErrorText } from "../lib/errors";

const { t } = useI18n();
const errorText = useErrorText();
const route = useRoute();
const router = useRouter();

const step = ref("credentials");
const email = ref("");
const password = ref("");
const otp = ref("");
const code = ref("");
const backup = ref(false);
const trustDevice = ref(false);
const busy = ref(false);
const error = ref("");
const notice = ref("");

onMounted(() => {
  // Handed over in history state by AcceptInvite/SignUp, never in the URL.
  const state = window.history.state || {};
  if (state.email) email.value = state.email;
  if (state.notice) notice.value = state.notice;
});

function done() {
  const raw = route.query.next;
  const next = typeof raw === "string" && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
  router.push(next || { name: "apps" });
}

async function run(fn) {
  busy.value = true;
  error.value = "";
  try {
    await fn();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

async function advance() {
  const outcome = await signInWithPassword(email.value, password.value);
  if (outcome === "signed_in") {
    password.value = "";
    done();
  } else if (outcome === "totp") {
    step.value = "totp";
  } else {
    await authApi.sendVerificationOtp(email.value);
    step.value = "verify";
  }
}

const submitCredentials = () => run(async () => {
  notice.value = "";
  await advance();
});

const submitOtp = () => run(async () => {
  const signedIn = await verifyEmailOtp(email.value, otp.value);
  otp.value = "";
  if (signedIn) done();
  else await advance();
});

const resendOtp = () => run(async () => {
  await authApi.sendVerificationOtp(email.value);
  notice.value = t("auth.otpResent");
});

const submitTotp = () => run(async () => {
  await completeTwoFactor(code.value, { backup: backup.value, trustDevice: trustDevice.value });
  password.value = "";
  done();
});

function toggleBackup() {
  backup.value = !backup.value;
  code.value = "";
  error.value = "";
}

function restart() {
  step.value = "credentials";
  password.value = "";
  otp.value = "";
  code.value = "";
  error.value = "";
  notice.value = "";
}
</script>
